import { useCallback, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Sparkles } from "lucide-react";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import { useSecondaryNavActions, useSecondaryNavTitle } from "@client/components/secondary-nav";
import {
  ListFilterSelect,
  ListPageHeaderActions,
  ListSearchInput,
} from "@client/components/common/list-page-controls";
import { ClassificationFieldDialog } from "@client/components/semantic-platform/data-governance/classification-field-dialog";
import {
  ClassificationFieldStatusEnum,
  ClassificationSubjectTypeEnum,
} from "@tubo/api-models/business/platform/metadata-governance.enums";
import { useListClassificationFields } from "@tubo/api-models/hooks/classification-governance-api.hooks";
import type { ClassificationFieldResp } from "@tubo/api-models/hooks/classification-governance-api.schemas";
import { useValueVocabularyList } from "@tubo/api-models/hooks/value-vocabulary-api.hooks";
import { NS_CONTEXT_ALL, NS_CONTEXT_GLOBAL } from "@tubo/semantic-concepts";
import { useSemanticNamespaceList } from "@tubo/api-models/hooks/semantic-namespace-api.hooks";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";

export const Route = createFileRoute("/_app/semantic-platform/tag-center/fields/")({
  // 领域上下文入 URL（分类字段经 vocabularyCode→数据字典 派生领域过滤）
  validateSearch: (search: Record<string, unknown>): { ns?: string } => {
    return typeof search.ns === "string" && search.ns ? { ns: search.ns } : {};
  },
  component: TagFieldsPage,
});

const SUBJECT_LABELS: Record<string, string> = {
  file: "文件",
  kbDoc: "知识库文档",
  modelEntity: "模型实体",
  fact: "事实",
};

const REVIEW_LABELS: Record<string, string> = {
  autoAccept: "高置信自动生效",
  manualReview: "人工确认",
  manualOnly: "仅人工",
};

const PAGE_SIZE = 20;

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "状态：全部" },
  { value: ClassificationFieldStatusEnum.Active, label: "状态：已启用" },
  { value: ClassificationFieldStatusEnum.Draft, label: "状态：草稿" },
  { value: ClassificationFieldStatusEnum.Disabled, label: "状态：已停用" },
];

const SUBJECT_FILTER_OPTIONS = [
  { value: "all", label: "对象：全部" },
  ...Object.values(ClassificationSubjectTypeEnum).map((value) => ({
    value,
    label: `对象：${SUBJECT_LABELS[value] ?? value}`,
  })),
];

/** 新建对话框的模式：空白表单 或 从数据字典新建（两步向导第一步选字典） */
type CreateMode = "blank" | "fromVocab";

function TagFieldsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState<CreateMode>("blank");
  const [editing, setEditing] = useState<ClassificationFieldResp | null>(null);
  const [inputValue, setInputValue] = useState("");
  const isComposing = useRef(false);
  const handleSearchConfirm = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);
  // 领域上下文（经 vocabularyCode→数据字典 派生领域过滤；__all__ 不传=全部）
  const { nsFilter, setNs } = useNsContext();
  const { data: vocabularies = [] } = useValueVocabularyList(undefined, { query: {} });
  const { data: namespaces = [] } = useSemanticNamespaceList({ query: {} });
  const nsNameById = useMemo(() => new Map(namespaces.map((ns) => [ns.id, ns.name])), [namespaces]);
  const vocabNsById = useMemo(() => new Map(vocabularies.map((v) => [v.code, v.namespaceId ?? null])), [vocabularies]);
  const { data = [], isLoading } = useListClassificationFields(
    {
      ...(search ? { search } : {}),
      ...(nsFilter !== NS_CONTEXT_ALL ? { ns: nsFilter } : {}),
    },
    { query: {} },
  );

  const filteredFields = useMemo(
    () => data.filter((field) => {
      const matchesStatus = statusFilter === "all" || field.status === statusFilter;
      const matchesSubject = subjectFilter === "all" || field.subjectTypes?.includes(subjectFilter);
      return matchesStatus && matchesSubject;
    }),
    [data, statusFilter, subjectFilter],
  );
  const pageCount = Math.max(1, Math.ceil(filteredFields.length / pageSize));
  const visibleFields = filteredFields.slice((Math.min(page, pageCount) - 1) * pageSize, Math.min(page, pageCount) * pageSize);
  const openedCountByVocabCode = useMemo(() => {
    const counts = new Map<string, number>();
    for (const field of data) {
      counts.set(field.vocabularyCode, (counts.get(field.vocabularyCode) ?? 0) + 1);
    }
    return counts;
  }, [data]);

  useSecondaryNavTitle(useMemo(() => <span className="text-sm font-semibold">标签字段</span>, []));
  useSecondaryNavActions(useMemo(() => (
    <ListPageHeaderActions>
      <ListFilterSelect
        items={STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onValueChange={(value) => { setStatusFilter(value ?? "all"); setPage(1); }}
      />
      <ListFilterSelect
        items={SUBJECT_FILTER_OPTIONS}
        value={subjectFilter}
        onValueChange={(value) => { setSubjectFilter(value ?? "all"); setPage(1); }}
      />
      <ListSearchInput
        placeholder="搜索名称、编码或数据字典"
        className="w-56"
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
          if (isComposing.current) return;
          handleSearchConfirm(event.target.value);
        }}
        onCompositionStart={() => { isComposing.current = true; }}
        onCompositionEnd={(event) => {
          isComposing.current = false;
          handleSearchConfirm(event.currentTarget.value);
        }}
        onBlur={(event) => {
          if (!isComposing.current) handleSearchConfirm(event.currentTarget.value);
        }}
      />
      <Button variant="outline" size="sm" onClick={() => { setCreateMode("fromVocab"); setCreateOpen(true); }}>
        <Sparkles className="mr-1 size-4" />从数据字典新建
      </Button>
      <Button size="sm" onClick={() => { setCreateMode("blank"); setCreateOpen(true); }}>
        <Plus className="mr-1 size-4" />新建标签字段
      </Button>
    </ListPageHeaderActions>
  ), [inputValue, statusFilter, subjectFilter, setCreateOpen, handleSearchConfirm]));

  const columns = useMemo<DataTableColumn<ClassificationFieldResp>[]>(
    () => [
    { id: "name", header: "标签字段", cell: (row) => <div><div className="font-medium">{row.name}</div><code className="text-xs text-muted-foreground">{row.code}</code></div> },
    { id: "vocabulary", header: "数据字典", cell: (row) => <code className="text-xs">{row.vocabularyCode}</code> },
    { id: "domain", header: "所属领域", cell: (row) => { const nsId = vocabNsById.get(row.vocabularyCode); const name = nsId ? nsNameById.get(nsId) : undefined; return name ? <Badge variant="outline">{name}</Badge> : <span className="text-xs text-muted-foreground">全局</span>; } },
    { id: "subjects", header: "适用对象", cell: (row) => <div className="flex flex-wrap gap-1">{(row.subjectTypes ?? []).map((subject) => <Badge key={subject} variant="outline">{SUBJECT_LABELS[subject] ?? subject}</Badge>)}</div> },
    { id: "scope", header: "作用域", cell: (row) => row.contextMode === "kbScoped" ? "知识库上下文" : "全局" },
    { id: "cardinality", header: "取值", cell: (row) => row.cardinality === "multiple" ? "多选" : "单选" },
    { id: "review", header: "审核", cell: (row) => REVIEW_LABELS[row.reviewPolicy] ?? row.reviewPolicy },
    { id: "status", header: "状态", cell: (row) => <Badge variant={row.status === "active" ? "secondary" : "outline"}>{row.status === "active" ? "已启用" : row.status === "draft" ? "草稿" : "已停用"}</Badge> },
    { id: "actions", header: "操作", cell: (row) => <Button variant="ghost" size="icon-sm" aria-label={`编辑${row.name}`} onClick={() => setEditing(row)}><Pencil className="size-4" /></Button> },
    ],
    [nsNameById, vocabNsById],
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <DataTable
        data={visibleFields}
        columns={columns}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        page={Math.min(page, pageCount)}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={filteredFields.length}
        onPageChange={setPage}
        emptyState={<div className="py-10 text-center text-sm text-muted-foreground"><p>当前领域下暂无标签字段</p><p className="mt-1 text-xs">标签字段归属其数据字典所在的领域——可切换领域，或切到「全部领域」查看全部；也可从数据字典一键新建（值域自动预填）。</p></div>}
      />
      {/* 新建（空白 / 从数据字典两步向导）与编辑共用本组件 */}
      <ClassificationFieldDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        pickVocabulary={createMode === "fromVocab"}
        vocabularies={vocabularies}
        openedCountByVocabCode={openedCountByVocabCode}
        onVocabularyPicked={(vocab) => {
          // 字段跟随数据字典领域：选定字典即切换上下文，创建后立即可见（全局字典回全局）
          setNs(vocab.namespaceId ?? NS_CONTEXT_GLOBAL);
        }}
      />
      <ClassificationFieldDialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)} editing={editing} />
    </div>
  );
}
